import React, { useEffect, useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Divider,
  Avatar,
  AvatarGroup,
  useTheme,
  useMediaQuery,
  Paper,
  Collapse,
  TextField,
  MenuItem,
  FormControl,
  Select
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  SportsEsports as GameIcon,
  TrendingUp as RemontadaIcon,
  LocalFireDepartment as PalizaIcon,
  Extension as ComboIcon,
  Shield as ResilienciaIcon,
  Person as SobrevivienteIcon,
  Bolt as RapidaIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  CalendarMonth as CalendarMonthIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import MatchCard from '../components/MatchCard';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { debounce } from 'lodash';
import SectionTitle from '../components/SectionTitle';
import PageTitle from '../components/PageTitle';
import CustomPlayerInput from '../components/CustomPlayerInput';

function HomePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, isAuthenticating } = useContext(AuthContext);
  const [originalMatches, setOriginalMatches] = useState([]);
  const [matches, setMatches] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [hasDecks, setHasDecks] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [searchDate, setSearchDate] = useState(null);
  const [team1Players, setTeam1Players] = useState([]);
  const [team2Players, setTeam2Players] = useState([]);
  const [team1SearchInput, setTeam1SearchInput] = useState('');
  const [team2SearchInput, setTeam2SearchInput] = useState('');
  const [team1Options, setTeam1Options] = useState([]);
  const [team2Options, setTeam2Options] = useState([]);
  const [isSearching1, setIsSearching1] = useState(false);
  const [isSearching2, setIsSearching2] = useState(false);
  const [activeInput, setActiveInput] = useState(1);
  const [team1SelectedDecks, setTeam1SelectedDecks] = useState([]);
  const [team2SelectedDecks, setTeam2SelectedDecks] = useState([]);
  const datePickerRef = useRef(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Limpiar cualquier estilo residual al montar/desmontar el componente
  useEffect(() => {
    document.body.style.removeProperty('padding-right');
    document.body.style.removeProperty('overflow');
    return () => {
      document.body.style.removeProperty('padding-right');
      document.body.style.removeProperty('overflow');
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const initializePage = async () => {
      if (isAuthenticating) {
        return;
      }

      if (!user) {
        navigate('/login');
        return;
      }

      setLoading(true);

      try {
        console.log('1. Starting initialization...');
        console.log('2. User token:', user.token);
        console.log('3. API base URL:', api.defaults.baseURL);
        console.log('4. API headers:', api.defaults.headers);

        console.log('5. Fetching decks...');
        const decksRes = await api.get('/api/decks');
        if (!mounted) return;
        console.log('6. Decks response:', decksRes.data);
        setHasDecks(decksRes.data.length > 0);

        console.log('7. Fetching approved matches...');
        const matchesRes = await api.get('/api/matches/all-approved');
        if (!mounted) return;
        console.log('8. Approved matches response:', matchesRes.data);
        setOriginalMatches(matchesRes.data);
        setMatches(matchesRes.data);

        console.log('9. Fetching users...');
        const usersRes = await api.get('/api/users');
        if (!mounted) return;
        console.log('10. Users response:', usersRes.data);
        const userMap = {};
        usersRes.data.forEach(u => {
          userMap[u.id] = u.username;
        });
        setUsersMap(userMap);
      } catch (error) {
        console.error('Error loading data:', error);
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // Escuchar el evento de aprobación de partidas
    const handleMatchApproved = () => {
      initializePage();
    };

    window.addEventListener('matchApproved', handleMatchApproved);
    initializePage();

    return () => {
      mounted = false;
      window.removeEventListener('matchApproved', handleMatchApproved);
    };
  }, [user, isAuthenticating, navigate]);

  // Agregar efecto para reiniciar el scroll
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Agregar las funciones de búsqueda de jugadores
  const searchTeam1Users = async (query) => {
    if (!query || query.length < 1) {
      return [];
    }
    
    setIsSearching1(true);
    
    try {
      let endpoint;
      if (!isNaN(query) && query.trim() !== '') {
        endpoint = `/api/users/search?id=${query}`;
      } else if (query.startsWith('@')) {
        const usernameQuery = query.substring(1);
        if (usernameQuery.trim() !== '') {
          endpoint = `/api/users/search?username=${usernameQuery}`;
        } else {
          return [];
        }
      } else {
        endpoint = `/api/users/search?username=${query}`;
      }
      
      const response = await api.get(endpoint);
      setTeam1Options(response.data || []);
      setIsSearching1(false);
      return response.data || [];
    } catch (error) {
      console.error('Error in searchTeam1Users:', error);
      setIsSearching1(false);
      return [];
    }
  };

  const searchTeam2Users = async (query) => {
    if (!query || query.length < 1) {
      return [];
    }
    
    setIsSearching2(true);
    
    try {
      let endpoint;
      if (!isNaN(query) && query.trim() !== '') {
        endpoint = `/api/users/search?id=${query}`;
      } else if (query.startsWith('@')) {
        const usernameQuery = query.substring(1);
        if (usernameQuery.trim() !== '') {
          endpoint = `/api/users/search?username=${usernameQuery}`;
        } else {
          return [];
        }
      } else {
        endpoint = `/api/users/search?username=${query}`;
      }
      
      const response = await api.get(endpoint);
      setTeam2Options(response.data || []);
      setIsSearching2(false);
      return response.data || [];
    } catch (error) {
      console.error('Error in searchTeam2Users:', error);
      setIsSearching2(false);
      return [];
    }
  };

  // Crear funciones debounce separadas
  const debouncedTeam1Search = React.useRef(
    debounce((query) => {
      return searchTeam1Users(query);
    }, 300)
  ).current;

  const debouncedTeam2Search = React.useRef(
    debounce((query) => {
      return searchTeam2Users(query);
    }, 300)
  ).current;

  // Efectos para la búsqueda
  useEffect(() => {
    if (team1SearchInput) {
      debouncedTeam1Search(team1SearchInput);
    } else {
      setTeam1Options([]);
    }
  }, [team1SearchInput]);

  useEffect(() => {
    if (team2SearchInput) {
      debouncedTeam2Search(team2SearchInput);
    } else {
      setTeam2Options([]);
    }
  }, [team2SearchInput]);

  // Agregar la función de búsqueda
  const handleSearch = async () => {
    try {
      console.log('Search date:', searchDate);
      console.log('Team 1 players:', team1Players);
      console.log('Team 2 players:', team2Players);
      
      let filteredMatches = [...originalMatches]; // Siempre empezar con las partidas originales

      // Filtrar por fecha si se proporciona
      if (searchDate) {
        const startDate = new Date(searchDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(searchDate);
        endDate.setHours(23, 59, 59, 999);
        
        console.log('Start date:', startDate);
        console.log('End date:', endDate);
        
        filteredMatches = filteredMatches.filter(match => {
          const matchDate = new Date(match.match_date);
          console.log('Match date:', matchDate);
          const isInRange = matchDate >= startDate && matchDate <= endDate;
          console.log('Is in range:', isInRange);
          return isInRange;
        });
      }
      
      // Función auxiliar para verificar si un jugador está en un equipo
      const isPlayerInTeam = (playerUsername, teamPlayers) => {
        const found = teamPlayers.includes(playerUsername);
        console.log(`Checking if ${playerUsername} is in team:`, teamPlayers, 'Result:', found);
        return found;
      };

      // Función auxiliar para verificar si todos los jugadores buscados están en los equipos
      const arePlayersInTeams = (usernames, team1Players, team2Players) => {
        console.log('Checking players:', usernames);
        console.log('Team 1 players:', team1Players);
        console.log('Team 2 players:', team2Players);
        
        const result = usernames.every(username => {
          const inTeam1 = isPlayerInTeam(username, team1Players);
          const inTeam2 = isPlayerInTeam(username, team2Players);
          const found = inTeam1 || inTeam2;
          console.log(`Player ${username} found in team 1: ${inTeam1}, team 2: ${inTeam2}, total: ${found}`);
          return found;
        });
        
        console.log('Final result for all players:', result);
        return result;
      };

      // Combinar todos los jugadores buscados
      const allSearchedPlayers = [
        ...team1Players.map(p => p.username),
        ...team2Players.map(p => p.username)
      ];

      // Solo aplicar el filtro de jugadores si hay al menos un jugador buscado
      if (allSearchedPlayers.length > 0) {
        console.log('Filtering by all players:', allSearchedPlayers);
        
        filteredMatches = filteredMatches.filter(match => {
          const matchTeam1Players = match.team1.players.map(p => usersMap[p.user_id]);
          const matchTeam2Players = match.team2.players.map(p => usersMap[p.user_id]);
          
          console.log('Processing match:', match.id);
          console.log('Match Team 1 players:', matchTeam1Players);
          console.log('Match Team 2 players:', matchTeam2Players);
          
          // Verificar si todos los jugadores buscados están en cualquiera de los equipos
          const allPlayersFound = arePlayersInTeams(
            allSearchedPlayers,
            matchTeam1Players,
            matchTeam2Players
          );
          
          console.log('Match result:', allPlayersFound);
          return allPlayersFound;
        });
      }

      // Filtrar por mazos si se proporcionan
      if (team1SelectedDecks.length > 0) {
        const team1DeckIds = team1SelectedDecks
          .map(deck => deck.deckId)
          .filter(id => id !== null); // Ignorar los mazos con id null (Cualquier Mazo)
        
        console.log('Filtering by Team 1 deck IDs:', team1DeckIds);
        
        // Solo aplicar el filtro si hay mazos específicos seleccionados
        if (team1DeckIds.length > 0) {
          filteredMatches = filteredMatches.filter(match => {
            const matchDeckIds = match.team1.decks.map(deck => deck.deck_id);
            console.log('Match Team 1 deck IDs:', matchDeckIds);
            const allDecksFound = team1DeckIds.every(id => matchDeckIds.includes(id));
            console.log('All Team 1 decks found:', allDecksFound);
            return allDecksFound;
          });
        }
      }
      
      if (team2SelectedDecks.length > 0) {
        const team2DeckIds = team2SelectedDecks
          .map(deck => deck.deckId)
          .filter(id => id !== null); // Ignorar los mazos con id null (Cualquier Mazo)
        
        console.log('Filtering by Team 2 deck IDs:', team2DeckIds);
        
        // Solo aplicar el filtro si hay mazos específicos seleccionados
        if (team2DeckIds.length > 0) {
          filteredMatches = filteredMatches.filter(match => {
            const matchDeckIds = match.team2.decks.map(deck => deck.deck_id);
            console.log('Match Team 2 deck IDs:', matchDeckIds);
            const allDecksFound = team2DeckIds.every(id => matchDeckIds.includes(id));
            console.log('All Team 2 decks found:', allDecksFound);
            return allDecksFound;
          });
        }
      }

      console.log('Final filtered matches count:', filteredMatches.length);
      setMatches(filteredMatches);
    } catch (error) {
      console.error('Error searching matches:', error);
    }
  };

  // Función para obtener el mazo por defecto de un usuario
  const fetchUserDefaultDeck = async (userId) => {
    try {
      const response = await api.get(`/api/decks/default/${userId}`, { 
        withCredentials: true 
      });
      return response.data;
    } catch (error) {
      console.warn(`No default deck found for user ${userId}`);
      return null;
    }
  };

  // Actualizar handleTeam1Select para incluir la selección del mazo por defecto
  const handleTeam1Select = async (option) => {
    if (team1Players.some(player => player.id === option.id)) {
      return;
    }

    // En la HomePage, siempre seleccionamos "Cualquier Mazo" por defecto
    const newDeckData = {
      playerId: option.id,
      deckId: null,
      deckName: "Cualquier Mazo",
      deckRaza: null
    };
    
    setTeam1SelectedDecks(prev => {
      const filtered = prev.filter(d => d.playerId !== option.id);
      return [...filtered, newDeckData];
    });
    
    setTeam1Players([...team1Players, option]);
    setTeam1SearchInput('');
    setTeam1Options([]);
    
    // Habilitar el input del equipo 2
    setActiveInput(2);
  };

  // Actualizar handleTeam2Select de manera similar
  const handleTeam2Select = async (option) => {
    if (team2Players.some(player => player.id === option.id)) {
      return;
    }

    // En la HomePage, siempre seleccionamos "Cualquier Mazo" por defecto
    const newDeckData = {
      playerId: option.id,
      deckId: null,
      deckName: "Cualquier Mazo",
      deckRaza: null
    };
    
    setTeam2SelectedDecks(prev => {
      const filtered = prev.filter(d => d.playerId !== option.id);
      return [...filtered, newDeckData];
    });
    
    setTeam2Players([...team2Players, option]);
    setTeam2SearchInput('');
    setTeam2Options([]);
    
    // Deshabilitar ambos inputs cuando se completa el equipo 2
    setActiveInput(null);
  };

  // Actualizar handleRemoveUser para manejar el activeInput
  const handleRemoveUser = (userId, teamNumber) => {
    if (teamNumber === 1) {
      setTeam1Players(team1Players.filter(player => player.id !== userId));
      setTeam1SelectedDecks(team1SelectedDecks.filter(deck => deck.playerId !== userId));
      // Habilitar el input del equipo 1 cuando se elimina un jugador
      setActiveInput(1);
    } else {
      setTeam2Players(team2Players.filter(player => player.id !== userId));
      setTeam2SelectedDecks(team2SelectedDecks.filter(deck => deck.playerId !== userId));
      // Habilitar el input del equipo 2 cuando se elimina un jugador
      setActiveInput(2);
    }
  };

  // Agregar la función para resetear la búsqueda
  const resetSearch = async () => {
    try {
      console.log('Resetting search...');
      // Resetear todos los estados de búsqueda
      setSearchDate(null);
      setTeam1Players([]);
      setTeam2Players([]);
      setTeam1SearchInput('');
      setTeam2SearchInput('');
      setTeam1Options([]);
      setTeam2Options([]);
      setTeam1SelectedDecks([]);
      setTeam2SelectedDecks([]);
      setActiveInput(1);
      
      // Restaurar las partidas originales
      setMatches(originalMatches);
    } catch (error) {
      console.error('Error resetting search:', error);
    }
  };

  if (isAuthenticating) {
    return (
      <Box sx={{ p: 3, mt: '64px', mb: '56px' }}>
        <LoadingSkeleton />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, mt: '64px', mb: '56px' }}>
        <LoadingSkeleton />
      </Box>
    );
  }

  if (!hasDecks) {
    return (
      <WelcomeScreen navigate={navigate} />
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      maxWidth: '1000px',
      mx: 'auto',
      bgcolor: 'transparent'
    }}>
      <Stack spacing={1} sx={{ width: '100%', bgcolor: 'transparent' }}>
        {/* Header Section */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          width: '100%',
          bgcolor: 'transparent',
          mb: 1
        }}>
          <PageTitle title="Partidas Recientes" />
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            width: '100%'
          }}>
            <Button
              variant={showSearch ? "contained" : "outlined"}
              startIcon={showSearch ? <CloseIcon /> : <SearchIcon />}
              onClick={() => setShowSearch(!showSearch)}
              sx={{
                width: '90%',
                borderRadius: 2,
                textTransform: 'none',
                borderColor: 'primary.main',
                color: showSearch ? 'white' : 'primary.main',
                bgcolor: showSearch ? 'primary.dark' : 'transparent',
                '&:hover': {
                  borderColor: 'primary.dark',
                  bgcolor: showSearch ? 'primary.darker' : 'primary.main',
                  color: 'white'
                }
              }}
            >
              {showSearch ? 'Cerrar Búsqueda' : 'Buscar Partidas'}
            </Button>
          </Box>
        </Box>

        {/* Search Panel */}
        <Collapse in={showSearch}>
          <Box sx={{ width: '90%', mx: 'auto' }}>
            <SectionTitle 
              title="Buscar Partidas"
              action={
                <IconButton 
                  onClick={() => setShowSearch(false)}
                  size="small"
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'text.primary'
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              }
            >
              <Stack spacing={2}>
                {/* Date Picker */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Fecha
                  </Typography>
                  <Box sx={{ position: 'relative' }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                      <DatePicker
                        value={searchDate}
                        onChange={(newValue) => {
                          setSearchDate(newValue);
                          setIsCalendarOpen(false);
                        }}
                        onAccept={(newValue) => {
                          setSearchDate(newValue || new Date());
                          setIsCalendarOpen(false);
                        }}
                        maxDate={new Date()}
                        open={isCalendarOpen}
                        onClose={() => setIsCalendarOpen(false)}
                        showTodayButton={false}
                        disableHighlightToday
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                            variant: 'outlined',
                            placeholder: '',
                            InputProps: {
                              startAdornment: (
                                <IconButton
                                  sx={{ color: 'primary.main', mr: 1 }}
                                  onClick={() => setIsCalendarOpen(true)}
                                  tabIndex={-1}
                                  edge="start"
                                >
                                  <CalendarMonthIcon />
                                </IconButton>
                              ),
                              endAdornment: searchDate ? (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSearchDate(null);
                                  }}
                                  sx={{
                                    color: 'error.main',
                                    opacity: 0.7,
                                    '&:hover': {
                                      opacity: 1
                                    }
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            },
                            inputRef: datePickerRef,
                            onClick: (e) => {
                              if (e.target.tagName !== 'BUTTON') {
                                setIsCalendarOpen(true);
                              }
                            },
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                borderColor: 'divider',
                                height: '40px',
                                bgcolor: 'background.paper',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  bgcolor: 'action.hover'
                                },
                                '&.Mui-focused': {
                                  borderColor: 'primary.main',
                                  boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)'
                                }
                              }
                            }
                          },
                          actionBar: {
                            actions: ['cancel']
                          }
                        }}
                        slots={{
                          openPickerButton: null
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Box>

                {/* Team 1 Search */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Equipo 1
                  </Typography>
                  <CustomPlayerInput
                    teamNumber={1}
                    players={team1Players}
                    options={team1Options}
                    searchValue={team1SearchInput}
                    handleSearch={setTeam1SearchInput}
                    handleSelectOption={handleTeam1Select}
                    selectedDecks={team1SelectedDecks}
                    matchMode="1v1"
                    activeInput={activeInput}
                    handleRemoveUser={handleRemoveUser}
                    setTeam1SelectedDecks={setTeam1SelectedDecks}
                    setTeam2SelectedDecks={setTeam2SelectedDecks}
                    setActiveInput={setActiveInput}
                    isSearching={isSearching1}
                    showAnyDeckOption={true}
                  />
                </Box>

                {/* Team 2 Search */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Equipo 2
                  </Typography>
                  <CustomPlayerInput
                    teamNumber={2}
                    players={team2Players}
                    options={team2Options}
                    searchValue={team2SearchInput}
                    handleSearch={setTeam2SearchInput}
                    handleSelectOption={handleTeam2Select}
                    selectedDecks={team2SelectedDecks}
                    matchMode="1v1"
                    activeInput={activeInput}
                    handleRemoveUser={handleRemoveUser}
                    setTeam1SelectedDecks={setTeam1SelectedDecks}
                    setTeam2SelectedDecks={setTeam2SelectedDecks}
                    setActiveInput={setActiveInput}
                    isSearching={isSearching2}
                    showAnyDeckOption={true}
                  />
                </Box>

                {/* Search Button */}
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  mt: 1,
                  position: 'relative'
                }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    sx={{
                      py: 1.2,
                      px: 4,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      boxShadow: 'none',
                      minWidth: '200px',
                      bgcolor: 'primary.main',
                      '&:hover': {
                        boxShadow: 'none',
                        bgcolor: 'primary.dark'
                      }
                    }}
                  >
                    Buscar Partidas
                  </Button>
                  <Button
                    variant="text"
                    onClick={resetSearch}
                    sx={{
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      fontWeight: 'medium',
                      color: 'text.secondary',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        color: 'text.primary'
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Resetear búsqueda
                  </Button>
                </Box>
              </Stack>
            </SectionTitle>
          </Box>
        </Collapse>

        {/* Matches Grid */}
        <Grid 
          container 
          spacing={1.2}
          justifyContent="center"
          alignItems="stretch"
          sx={{ 
            width: '100%',
            margin: '0 auto'
          }}
        >
          {matches.map((match) => (
            <Grid item xs={12} sm={6} md={4} key={match.id} sx={{
              display: 'flex',
              justifyContent: 'center'
            }}>
              <Box sx={{ width: '90%' }}>
                <MatchCard 
                  match={match} 
                  usersMap={usersMap}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Box>
  );
}

// Loading skeleton component
function LoadingSkeleton() {
  return (
    <Stack spacing={3} sx={{ bgcolor: 'transparent' }}>
      {/* Título con animación de pulso */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        bgcolor: 'transparent',
        mb: 1,
        mt: 0
      }}>
        <Box 
          sx={{ 
            width: '200px', 
            height: '40px', 
            borderRadius: '4px',
            background: 'linear-gradient(90deg, rgba(30, 30, 30, 0.2) 25%, rgba(30, 30, 30, 0.4) 50%, rgba(30, 30, 30, 0.2) 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            '@keyframes shimmer': {
              '0%': { backgroundPosition: '200% 0' },
              '100%': { backgroundPosition: '-200% 0' }
            }
          }} 
        />
      </Box>
      
      {/* Grid de tarjetas con efecto de pulso */}
      <Grid container spacing={1.2} justifyContent="center" alignItems="stretch">
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item} sx={{
            display: 'flex',
            justifyContent: 'center'
          }}>
            <Box sx={{ 
              width: '90%',
              height: '200px',
              borderRadius: '8px',
              overflow: 'hidden',
              position: 'relative',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              background: 'linear-gradient(135deg, rgba(30, 30, 30, 0.8) 0%, rgba(40, 40, 40, 0.9) 100%)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.05) 25%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 75%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s infinite',
                '@keyframes shimmer': {
                  '0%': { backgroundPosition: '200% 0' },
                  '100%': { backgroundPosition: '-200% 0' }
                }
              }
            }}>
              {/* Detalles de la tarjeta */}
              <Box sx={{ 
                position: 'absolute', 
                top: '10px', 
                left: '10px', 
                width: '60%',
                height: '20px',
                borderRadius: '4px',
                background: 'rgba(255, 255, 255, 0.1)',
                animation: 'pulse 1.5s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 0.3 },
                  '50%': { opacity: 0.5 },
                  '100%': { opacity: 0.3 }
                }
              }} />
              <Box sx={{ 
                position: 'absolute', 
                top: '40px', 
                left: '10px', 
                width: '40%',
                height: '15px',
                borderRadius: '4px',
                background: 'rgba(255, 255, 255, 0.1)',
                animation: 'pulse 1.5s infinite',
                animationDelay: '0.2s'
              }} />
              <Box sx={{ 
                position: 'absolute', 
                bottom: '10px', 
                left: '10px', 
                width: '30%',
                height: '15px',
                borderRadius: '4px',
                background: 'rgba(255, 255, 255, 0.1)',
                animation: 'pulse 1.5s infinite',
                animationDelay: '0.4s'
              }} />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

// Welcome screen component
function WelcomeScreen({ navigate }) {
  return (
    <Box sx={{ 
      p: 3,
      mt: '64px',
      mb: '56px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: 3,
      bgcolor: 'transparent'
    }}>
      <Typography variant="h4" fontWeight="bold" color="primary">
        ¡Bienvenido a PlayMitos!
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Para comenzar tu aventura, necesitas crear tu primer mazo.
      </Typography>
      <Button 
        variant="contained" 
        size="large"
        startIcon={<AddIcon />}
        onClick={() => navigate('/decks')}
      >
        Crear mi Primer Mazo
      </Button>
    </Box>
  );
}

export default HomePage;